import { type } from 'os';
import { TPackage } from './common';

export enum EConfigType {
    STRING = 'string',
    NUMBER = 'number',
    IMG = 'img',
}

export type TConfigModuleConfig = {
    type: EConfigType;
    name: string;
};

export enum EConfigCtaType {
    SELECT = 'S',
    TEXT = 'T',
}

export type TCta = {
    id: number;
    name: string;
    imageUrl?: string;
    aiPrompt?: string;
    placeholder?: string;
    isDefault?: boolean; //is init
    type: EConfigCtaType;
};

export enum EConfigMoudleType {
    LIST = 'L',
    TEXT = 'T',
}

export type TConfigModule = {
    id: number;
    iconUrl?: string;
    name: string;
    description: string;
    type: EConfigMoudleType;
    ctas?: TCta[];
    canSubmit?: boolean;
    onClick?: () => void;
    loading?: boolean;
    disabled?: boolean;
};

export enum EAIEditorType {
    ABOUTME = 'ABOUTME',
    PROSPECT = 'PROSPECT',
    TREATMENT_PHILOSOPHY = 'TREATMENT_PHILOSOPHY',
    PRACTICE_DESCRIPTION = 'PRACTICE_DESCRIPTION',
    HEADLINES = 'HEADLINES',
    TEXT_VISIT_REMINDER_MESSAGE = 'TEXT_VISIT_REMINDER_MESSAGE',
}

export enum EAIRichTextEditorType {
    TEXT_VISIT_TREATMENT = 'TEXT_VISIT_TREATMENT',
    TEXT_VISIT_NOTE_FOR_PROVIDER = 'TEXT_VISIT_NOTE_FOR_PROVIDER',
    TEXT_VISIT_FOLLOW_UP_MESSAGE = 'TEXT_VISIT_FOLLOW_UP_MESSAGE',
    AI_REVIEW_REPLY = 'AI_REVIEW_REPLY',
    AI_EDITOR_APPOINTMENT_TREATMENT = 'AI_EDITOR_APPOINTMENT_TREATMENT',
    AI_EDITOR_APPOINTMENT_NOTE_FOR_PROVIDER = 'AI_EDITOR_APPOINTMENT_NOTE_FOR_PROVIDER',
    TREATMENT_PLAN_TEMPLATE_GENERATE = 'TREATMENT_PLAN_TEMPLATE_GENERATE',
    TREATMENT_PLAN_ADDENDUM_GENERATE = 'TREATMENT_PLAN_ADDENDUM_GENERATE',
}

export type TFetchAiCtaResultRes = {
    ctaId: number;
    conversionId?: string;
    type: EAIEditorType;
    content?: string;
    prompt?: string;
    providerId?: number;
    relationId?: number;
};

export type TFeedbackAiResultRes = {
    type: EAIEditorType;
    messageId?: string;
    like: boolean;
};

export type TStreamingChatRes = {
    inputs: {};
    query: string;
    user: string;
    response_mode: 'streaming';
    conversation_id?: string;
    do_evaluation: false;
};

export type TStreamingChatRep = {
    event: string; // 'agent_message',
    conversation_id: string; //'570a0570-af66-42b5-ae2d-c228e46dd7e2',
    message_id: string; //'d4533480-d187-4b10-92a3-b773ec545e6a',
    created_at: number; //1715762134,
    task_id: string; // '589227e0-c2a9-4b02-a1c1-271a07a0c228',
    id: string; //'d4533480-d187-4b10-92a3-b773ec545e6a',
    answer?: string; //' in'
};

export type TProviderTableRes = {
    inputs: {
        conversation_id: string;
        message_id: string;
        user_id: string;
    };
    user: string;
};

export type TFeedbackMessageRes = {
    rating: 'like' | 'dislike';
    tag: string;
    comment: string;
    user: string;
};

export type TPromptMessage = {
    id: string; //'f2d93d63-c5c2-49d2-8d87-d56c8a0b9171',
    conversation_id: string; //'524baf4b-4d58-4421-b278-e1ab6f2e8169',
    inputs?: {};
    query: string; //'Look for a prescribe in TX who has experiences with ADHD medications. The provider’s initial session fee should be below $130.',
    answer: string; // 'I found a provider in Texas who has experience with ADHD medications and offers an initial session fee below $130:\n\n- **Michele Heyman, LPC**\n  - **Years of Experience:** 20 years\n  - **Specialty:** Mental Health\n  - **Conditions Treated:** Anxiety, Depression, ADD/ADHD, PTSD, Insomnia, ADHD\n  - **Initial Online Therapy Session Fee:** $129\n  - **Service Length:** 60 minutes\n  - **Type:** Video\n  - **States Licensed:** Texas (TX), New Mexico (NM)\n  - ![Michele Heyman, LPC](https://saas-prod-us.s3.us-east-2.amazonaws.com/prod/provider/image/airtable/michele-heyman.png)\n  - [Book an appointment with Michele Heyman, LPC](https://care.helloklarity.com/booking?state=TX&provider=6552b750ea5b4b59224992c1&service=9bd92ead-f510-460a-85ac-dd61997de506)\n\nPlease note that this session is conducted via video.',
    message_files?: string[];
    feedback?: string;
    retriever_resources?: string[];
    created_at?: number;
    agent_thoughts?: unknown[];
    status?: string; //'normal',
    error?: string;
};

export type TChatMessage = {
    query?: string; //'Look for a prescribe in TX who has experiences with ADHD medications. The provider’s initial session fee should be below $130.',
    answer?: string; // 'I found a provider in Texas who has experience with ADHD medications and offers an initial session fee below $130:\n\n- **Michele Heyman, LPC**\n  - **Years of Experience:** 20 years\n  - **Specialty:** Mental Health\n  - **Conditions Treated:** Anxiety, Depression, ADD/ADHD, PTSD, Insomnia, ADHD\n  - **Initial Online Therapy Session Fee:** $129\n  - **Service Length:** 60 minutes\n  - **Type:** Video\n  - **States Licensed:** Texas (TX), New Mexico (NM)\n  - ![Michele Heyman, LPC](https://saas-prod-us.s3.us-east-2.amazonaws.com/prod/provider/image/airtable/michele-heyman.png)\n  - [Book an appointment with Michele Heyman, LPC](https://care.helloklarity.com/booking?state=TX&provider=6552b750ea5b4b59224992c1&service=9bd92ead-f510-460a-85ac-dd61997de506)\n\nPlease note that this session is conducted via video.',
    providerIds?: string;
    messageId?: string;
    done?: boolean;
};

export type THistoryMessage = {
    id: string; // 'f2d93d63-c5c2-49d2-8d87-d56c8a0b9171',
    conversation_id: string; // '524baf4b-4d58-4421-b278-e1ab6f2e8169',
    inputs: {};
    query?: string; //'Look for a prescribe in TX who has experiences with ADHD medications. The provider’s initial session fee should be below $130.',
    answer?: string; // 'I found a provider in Texas who has experience with ADHD medications and offers an initial session fee below $130:\n\n- **Michele Heyman, LPC**\n  - **Years of Experience:** 20 years\n  - **Specialty:** Mental Health\n  - **Conditions Treated:** Anxiety, Depression, ADD/ADHD, PTSD, Insomnia, ADHD\n  - **Initial Online Therapy Session Fee:** $129\n  - **Service Length:** 60 minutes\n  - **Type:** Video\n  - **States Licensed:** Texas (TX), New Mexico (NM)\n  - ![Michele Heyman, LPC](https://saas-prod-us.s3.us-east-2.amazonaws.com/prod/provider/image/airtable/michele-heyman.png)\n  - [Book an appointment with Michele Heyman, LPC](https://care.helloklarity.com/booking?state=TX&provider=6552b750ea5b4b59224992c1&service=9bd92ead-f510-460a-85ac-dd61997de506)\n\nPlease note that this session is conducted via video.',
    feedback?: string;
    retriever_resources: string[];
    created_at: number;
    agent_thoughts: string[];
    status: string; // 'normal',
    error?: null;
};

export type TTableProvider = {
    providerId: string; // '6552b750ea5b4b59224992c1',
    source: 'A' | 'E';
    firstName: string; //'Michele',
    middleName: string; //'',
    credential: string;
    lastName: string; //'Heyman',
    photos: string[];
    inPerson: boolean;
    lowestPrice: number;
    highestPrice: number;
    initialList?: TPackage[];
    consultationList?: TPackage[];
};

export type TConversation = {
    created_at: number;
    id: string; //'3aa330af-2bfe-4b20-bf17-5d7b5262b8ec'
    inputs: {};
    introduction: string;
    name: string;
    status: string;
};

export type TTableData = {
    providers: TTableProvider[];
    states: string[];
};

export enum EAvaTone {
    FRIENDLY = 'Friendly',
    CASUAL = 'Casual',
    EMPATHETIC = 'Empathetic',
    FORMAL = 'Formal',
}

// {
//     "id": 3,
//     "providerId": 1446,
//     "messageTone": "ADJUST_LENGTH_FRIENDLY",
//     "isAiAssisted": true,
//     "customPrompt": "Write a warm and upbeat greeting to welcome a new patient. Always include [Patient Name], [Appointment Date], [Appointment Time], and [Appointment Type]. Add [Visit Reason] if available. Keep it friendly and encouraging. Remind the patient to complete intake forms in the portal. If a [Meeting Link] is provided, include it directly in the message. Remind the patient to save the provider’s practice phone number [Practice Phone Number], as the provider may call to confirm appointment details. End the message by including the provider’s name and credential.",
//     "greetingMessage": "",
//     "createdAt": "2025-11-25T06:21:59",
//     "updatedAt": "2025-11-25T06:24:30"
// }

export type TGreeting = {
    id: number;
    providerId: number;
    messageTone: EGreetingMessageTony;
    isAiAssisted: boolean;
    customPrompt?: string;
    greetingMessage?: string;
    createdAt: string;
    updatedAt: string;
    status?: 'A' | 'D';
};

export type TProspectAvaConfig = {
    aiPrompt: string; // "Ensure the tone is patient friendly and jargon free."
    avaAutoReply: boolean;
    avaTone: EAvaTone;
    avaToneId: number;
    id?: number;
    providerId: number;
    smsNotificationEnabled?: boolean;
    greeting?: TGreeting;
};
export type TNotifyInfo = {
    notificationEmail: string;
    notificationPhoneNumber: string;
    tel: string;
};

export enum EAiRole {
    USER = 'user',
    ASSISTANT = 'assistant',
    QUESTION = 'question',
}

//https://www.notion.so/klarityhealth/Provider-onboarding-26218650acdf80138ac8e2e76f438bac
export enum EAIChatPageId {
    PROVIDER_ONBOARDING_EMAIL_ENTRY = '1',
    PROVIDER_ONBOARDING_SERVICES_SELECTION = '2',
    PROVIDER_ONBOARDING_STATE_LICENSING = '3',
    PROVIDER_ONBOARDING_FINISH = '4',
    PROVIDER_ONBOARDING_PATIENT_VOLUME = '5',
    PROVIDER_ONBOARDING_REFERRAL_SOURCE = '6',
    PROVIDER_ONBOARDING_EHR_SYSTEM = '7',
    PROVIDER_ONBOARDING_SIGN_UP = '8',
    PROVIDER_HOME_APPOINTMENT_TRENDS = '9',
    PROVIDER_HOME = '10',
    PROVIDER_MESSAGE = '11',
    PROVIDER_MESSAGE_SETTING = '12',
    PROVIDER_CALENDAR = '13',
    PROVIDER_CALENDAR_SETTING = '14',
    PROVIDER_CLIENTS = '15',
    PROVIDER_CLIENTS_DETAIL = '16',
    PROVIDER_MARKETING = '17',
    PROVIDER_MARKETING_SETTING = '18',
    PROVIDER_TEXT_VISIT = '19',
    PROVIDER_FINANCIAL = '20',
    PROVIDER_PROFILE = '21',
    PROVIDER_FAQ = '22',
    PROVIDER_NO_SHOW_POLICY = '23',
    PROVIDER_SERVICE_TEMPLATE = '24',
    PROVIDER_MEDICATION = '25',
    PROVIDER_PRESCRIPTIONS = '26',
    // Create Profile 步骤组
    PROVIDER_ONBOARDING_PROFILE_INTRO = '27',
    PROVIDER_ONBOARDING_PROFILE_NAME = '28',
    PROVIDER_ONBOARDING_PROFILE_CREDENTIALS = '29',
    PROVIDER_ONBOARDING_PROFILE_GENDER = '30',
    // Setup Service 步骤组
    PROVIDER_ONBOARDING_SERVICE_INTRO = '31',
    PROVIDER_ONBOARDING_SERVICE_SPECIALTY = '32',
    PROVIDER_ONBOARDING_SERVICE_PAYMENT_METHOD = '33',
    PROVIDER_ONBOARDING_SERVICE_TEMPLATE = '34',
    // List on Klarity Health 步骤组
    PROVIDER_ONBOARDING_KLARITY_INTRO = '35',
    PROVIDER_ONBOARDING_KLARITY_SHOWCASE = '36',
    PROVIDER_ONBOARDING_KLARITY_PERSONAL_ID = '37',
    PROVIDER_ONBOARDING_KLARITY_MEDICAL_LICENSE = '38',
    PROVIDER_ONBOARDING_KLARITY_PAYMENT_INFO = '39',
    // Configure Availability 步骤组
    PROVIDER_ONBOARDING_AVAILABILITY_INTRO = '40',
    PROVIDER_ONBOARDING_AVAILABILITY_WORKING_HOURS = '41',
    PROVIDER_ONBOARDING_AVAILABILITY_ADVANCE_NOTICE = '42',
    PROVIDER_ONBOARDING_AVAILABILITY_CONSENT_FORMS = '43',
    PROVIDER_ONBOARDING_AVAILABILITY_CONSENT_FORMS_CONFIRM = '44',
    // Review Page
    PROVIDER_ONBOARDING_REVIEW = '45',
    // Finish Page 状态
    PROVIDER_ONBOARDING_FINISH_GO_LIVE = '46',
    PROVIDER_ONBOARDING_FINISH_APPROVED = '47',
    PROVIDER_ONBOARDING_FINISH_REJECTED = '48',
    PROVIDER_ONBOARDING_FINISH_REVIEWING = '49',
    // Advance Setting 步骤组
    PROVIDER_ONBOARDING_ADVANCE_SETTING_INTRO = '50',
    PROVIDER_ONBOARDING_ADVANCE_SETTING_PROFILE_ENRICH = '51',
    PROVIDER_ONBOARDING_ADVANCE_SETTING_PROLISTING_SETUP = '52',
    PROVIDER_ONBOARDING_ADVANCE_SETTING_GOOGLE_SYNC = '53',
    // Advance Setting Success Page
    PROVIDER_ONBOARDING_ADVANCE_SETTING_SUCCESS = '54',
    // Lead Form 新增步骤
    PROVIDER_ONBOARDING_BILLING_METHOD = '55',
    PROVIDER_ONBOARDING_EHR_ACCESS = '56',
    PROVIDER_ONBOARDING_NO_EHR_INFO = '57',
    // Add Group Provider 步骤组
    GROUP_PROVIDER_SETUP_SPECIALTY = '58',
    GROUP_PROVIDER_PAYMENT_METHOD = '59',
    GROUP_PROVIDER_SERVICE_TEMPLATE = '60',
    GROUP_PROVIDER_WORKING_HOURS = '61',
    GROUP_PROVIDER_ADVANCE_NOTICE = '62',
    GROUP_PROVIDER_ENRICH_PROFILE = '63',
    // Invited Provider Onboarding 步骤组（被邀请的 provider 自助 onboarding，区别于单人 onboarding）
    INVITED_PROVIDER_CREATE_PROFILE_CREDENTIALS = '64',
    INVITED_PROVIDER_CREATE_PROFILE_GENDER = '65',
    INVITED_PROVIDER_SETUP_SERVICES_SPECIALTY = '66',
    INVITED_PROVIDER_SETUP_SERVICES_PAYMENT = '67',
    INVITED_PROVIDER_SETUP_SERVICES_TEMPLATE = '68',
    INVITED_PROVIDER_MARKETING_PERSONAL_ID = '69',
    INVITED_PROVIDER_MARKETING_MEDICAL_LICENSE = '70',
    INVITED_PROVIDER_AVAILABILITY_WORKING_HOURS = '71',
    INVITED_PROVIDER_AVAILABILITY_ADVANCE_NOTICE = '72',
    INVITED_PROVIDER_REVIEW = '73',
    INVITED_PROVIDER_GO_LIVE = '74',
    INVITED_PROVIDER_ADVANCE_SETTING_INTRO = '75',
    INVITED_PROVIDER_ADVANCE_SETTING_PROFILE_ENRICH = '76',
    INVITED_PROVIDER_ADVANCE_SETTING_GOOGLE_SYNC = '77',
    // Lead Form 新增：Help type（What best describes you）选择步骤
    PROVIDER_ONBOARDING_HELP_TYPE = '78',
    // Credentialing 向导步骤组（PPACredentialingStartPage 每一步一个 AI 入口）
    CREDENTIALING_BASIC_INFO = '79',
    CREDENTIALING_LICENSES = '80',
    CREDENTIALING_PRACTICE = '81',
    CREDENTIALING_MALPRACTICE = '82',
    CREDENTIALING_BACKGROUND = '83',
    CREDENTIALING_REFERENCES = '84',
    CREDENTIALING_VERIFICATION = '85',
    CREDENTIALING_REVIEW = '86',
}

export type TAIUploadImage = {
    id: string; //'84d7d50b-5f2f-49ad-9bed-f228272de775',
    bot_id: string; //'provider_onboarding',
    content_type: string; //'image/png',
    url: string; //'https://cdn.helloklarity.net/ai-studio/bots/provider_onboarding/2025/09/03/20250903_001424_d2427ef1.png',
    size: number; //164988,
    file_type: string; // 'image',
    filename: string; //'image.png',
    base64: string;
    created_at: string; //'2025-09-03T00:14:24'
};

export type TAiMessage = {
    role: EAiRole; //'user'|"assistant",
    content: string; //'hello here',
    bot_id: string; // 'provider_onboarding',
    timestamp: string; // '2025-09-03T00:30:20.788498'
    file_ids?: string[]; // from server, not use now
    files?: TAIUploadImage[];
    blobs?: TAIUploadImage[]; // change to object instead of file, for better performance
};

export type TGetRichTextResultRes = {
    rewriteAction: string;
    type: string;
    content: string;
    prompt?: string;
    relationId: number;
    format?: 'TEXT' | 'HTML' | 'MARKDOWN';
    context?: Record<string, string>;
};

// ADJUST_LENGTH_FRIENDLY
// ADJUST_LENGTH_FORMAL
//  ADJUST_LENGTH_CASUAL
//  ADJUST_LENGTH_EMPATHETIC
export enum EGreetingMessageTony {
    ADJUST_LENGTH_FRIENDLY = 'ADJUST_LENGTH_FRIENDLY',
    ADJUST_LENGTH_FORMAL = 'ADJUST_LENGTH_FORMAL',
    ADJUST_LENGTH_CASUAL = 'ADJUST_LENGTH_CASUAL',
    ADJUST_LENGTH_EMPATHETIC = 'ADJUST_LENGTH_EMPATHETIC',
}
